import { OrganisationSearchQuestion } from '@percent/grants-nomination-form/api/nominationForm/nominationForm.types';
import { Organisation } from '@percent/grants-nomination-form/api/organisations/organisations.types';
import { useNominationForm } from '@percent/grants-nomination-form/hooks';
import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

// We need to store the metadata to display the details of the organisation and allow searching for it
export const ORGANISATION_QUESTION_METADATA = {
  QUERY: 'ORGANISATION_QUERY',
  ORGANISATION: 'ORGANISATION_METADATA',
  ADD_ORGANISATION_ENABLED: 'ADD_ORGANISATION_ENABLED',
} as const;

const setValueOptions = { shouldValidate: false, shouldTouch: false };

export const useOrganisationField = () => {
  const { setValue, resetField } = useFormContext();
  const { setVisibleQuestions, nominationFormData } = useNominationForm();

  const setOrganisationValue = (organisation: Organisation | undefined) => {
    setValue(ORGANISATION_QUESTION_METADATA.ORGANISATION, organisation, setValueOptions);
  };

  const addOrganisationQuestions = useMemo(() => {
    const organisationSearchQuestion = nominationFormData?.pages
      .flatMap((page) => page.questions)
      .find((question) => question?.type === 'organisation_search') as OrganisationSearchQuestion;

    return organisationSearchQuestion?.addOrganisationQuestions || [];
  }, [nominationFormData]);

  const setQueryValue = (query: string) => {
    setValue(ORGANISATION_QUESTION_METADATA.QUERY, query, setValueOptions);
    // Clear the organisation value when the query changes
    resetField(ORGANISATION_QUESTION_METADATA.ORGANISATION, { keepDirty: false, keepTouched: false });
  };

  const toggleAddOrganisation = (visible: boolean) => {
    setValue(ORGANISATION_QUESTION_METADATA.ADD_ORGANISATION_ENABLED, visible, setValueOptions);
    setVisibleQuestions(Object.fromEntries(addOrganisationQuestions.map((questionId) => [questionId, visible])));

    if (!visible) {
      addOrganisationQuestions.forEach((questionId) => {
        // BE returns ids so we need to look up for the question key
        const question = nominationFormData?.pages
          .flatMap((page) => page.questions)
          .find((question) => question?.id === questionId);

        if (question) {
          setValue(question.key, undefined, setValueOptions);
        }
      });
    }
  };

  return {
    organisation: useWatch({ name: ORGANISATION_QUESTION_METADATA.ORGANISATION }),
    query: useWatch({ name: ORGANISATION_QUESTION_METADATA.QUERY }),
    addOrganisationEnabled: useWatch({ name: ORGANISATION_QUESTION_METADATA.ADD_ORGANISATION_ENABLED }),
    setOrganisationValue,
    setQueryValue,
    toggleAddOrganisation,
  };
};
